import { useSetAtom } from 'jotai';
import { type UIEvent, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Group,
  Heading,
  Link,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Tooltip,
} from '@optiaxiom/react';
import { IconCheckDouble, IconXmark } from '@optimizely/axiom-icons';

import { activeRightSidebarAtom } from '@frontend/atoms';
import { useInstanceId, useNamespacedNavigate } from '@frontend/hooks';

import { useCursorNotificationList, useNotificationActions, useUnreadCount } from '../hooks';
import type { Notification } from '../types';
import {
  formatTimestamp,
  formatUnreadCount,
  getDeepLinkPath,
  getNotificationVisuals,
} from '../utils';

import { notificationListItemStyles } from './NotificationTray.css';

const PAGE_SIZE = 20;
const MAX_TRAY_NOTIFICATIONS = 40;

type NotificationTrayTab = 'all' | 'unread';

export function NotificationTray() {
  const [tab, setTab] = useState<NotificationTrayTab>('all');
  const setActiveRightSidebar = useSetAtom(activeRightSidebarAtom);
  const navigate = useNamespacedNavigate();
  const actions = useNotificationActions();
  const { data: unreadCount } = useUnreadCount();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } =
    useCursorNotificationList({
      limit: PAGE_SIZE,
      status: tab,
    });

  const notifications = useMemo(() => data?.pages.flatMap(page => page.items) ?? [], [data?.pages]);
  const reachedTrayLimit = notifications.length >= MAX_TRAY_NOTIFICATIONS;
  const canFetchMore = Boolean(hasNextPage && !reachedTrayLimit && !isFetchingNextPage);

  const closeSidebar = useCallback(() => {
    setActiveRightSidebar(null);
  }, [setActiveRightSidebar]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!canFetchMore) return;
      const target = event.currentTarget;
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceFromBottom < 160) {
        void fetchNextPage();
      }
    },
    [canFetchMore, fetchNextPage]
  );

  const handleActivate = useCallback(
    (notification: Notification) => {
      if (!notification.is_read) {
        actions.markAsRead.mutate(notification.id);
      }
      if (notification.type?.startsWith('messaging_')) {
        const meta = notification.metadata as Record<string, unknown> | undefined;
        document.dispatchEvent(
          new CustomEvent('opal::im::notificationClicked', {
            detail: {
              conversationId: meta?.conversation_id,
              messageId: meta?.message_id,
              msgThreadId: meta?.thread_id,
            },
            bubbles: true,
          })
        );
      }
      closeSidebar();
      navigate(getDeepLinkPath(notification));
    },
    [actions.markAsRead, closeSidebar, navigate]
  );

  const handleViewNotifications = useCallback(() => {
    closeSidebar();
    navigate('/notifications');
  }, [closeSidebar, navigate]);

  const handleMarkAllRead = useCallback(() => {
    actions.bulkMarkAsRead.mutate(undefined);
  }, [actions.bulkMarkAsRead]);

  const handleDelete = useCallback(
    (id: string) => actions.deleteNotification.mutate(id),
    [actions.deleteNotification]
  );

  const body = (
    <NotificationTrayList
      emptyMessage={tab === 'unread' ? 'No unread notifications' : 'No notifications'}
      isFetchingNextPage={isFetchingNextPage}
      isPending={isPending}
      notifications={notifications}
      onActivate={handleActivate}
      onDelete={handleDelete}
      onViewNotifications={handleViewNotifications}
    />
  );

  const unreadLabel = formatUnreadCount(unreadCount);

  return (
    <Group flexDirection="column" bg="bg.page" h="full">
      <Group alignItems="center" justifyContent="space-between" px="16" py="12" bg="bg.default">
        <Heading level="4" fontSize="lg" fontWeight="500">
          Notifications
        </Heading>

        <Tooltip content="Close notifications" delayDuration={0}>
          <Button
            appearance="subtle"
            aria-label="Close notifications"
            icon={<IconXmark />}
            onClick={closeSidebar}
            square
          />
        </Tooltip>
      </Group>

      <Tabs
        display="flex"
        flex="1"
        flexDirection="column"
        gap="8"
        h="full"
        onValueChange={value => setTab(value as NotificationTrayTab)}
        overflow="hidden"
        px="16"
        value={tab}
      >
        <TabsList alignItems="center" justifyContent="space-between" w="full">
          <Group>
            <TabsTrigger value="all" fontSize="sm" px="8">
              All
            </TabsTrigger>
            <TabsTrigger value="unread" fontSize="sm" px="8">
              {unreadLabel ? `Unread (${unreadLabel})` : 'Unread'}
            </TabsTrigger>
          </Group>
          <Button
            appearance="subtle"
            icon={<IconCheckDouble />}
            onClick={handleMarkAllRead}
            size="sm"
          >
            Mark all as read
          </Button>
        </TabsList>

        <TabsContent
          flex="1"
          onScroll={handleScroll}
          overflowY="auto"
          style={{ minHeight: 0 }}
          value="all"
        >
          {body}
        </TabsContent>
        <TabsContent
          flex="1"
          onScroll={handleScroll}
          overflowY="auto"
          style={{ minHeight: 0 }}
          value="unread"
        >
          {body}
        </TabsContent>
      </Tabs>
    </Group>
  );
}

type NotificationTrayListProps = {
  emptyMessage: string;
  isFetchingNextPage: boolean;
  isPending: boolean;
  notifications: Notification[];
  onActivate: (notification: Notification) => void;
  onDelete: (id: string) => void;
  onViewNotifications: () => void;
};

function NotificationTrayList({
  emptyMessage,
  isFetchingNextPage,
  isPending,
  notifications,
  onActivate,
  onDelete,
  onViewNotifications,
}: NotificationTrayListProps) {
  const { instanceId } = useInstanceId();

  if (isPending) {
    return (
      <Group flexDirection="column" gap="0" p="16">
        {Array.from({ length: 5 }, (_, index) => (
          <Group flexDirection="column" gap="8" key={index} py="16">
            <Skeleton h="16" w="3/4" />
            <Skeleton h="16" w="full" />
            <Skeleton h="16" w="1/2" />
          </Group>
        ))}
      </Group>
    );
  }

  if (notifications.length === 0) {
    return (
      <Group alignItems="center" justifyContent="center" p="32">
        <Text color="fg.tertiary">{emptyMessage}</Text>
      </Group>
    );
  }

  return (
    <Group flexDirection="column" gap="0">
      {notifications.map(notification => (
        <NotificationTrayItem
          key={notification.id}
          notification={notification}
          onActivate={onActivate}
          onDelete={onDelete}
        />
      ))}
      {isFetchingNextPage && (
        <Group flexDirection="column" gap="8" p="16">
          <Skeleton h="16" w="3/4" />
          <Skeleton h="16" w="full" />
        </Group>
      )}
      <Group alignItems="center" justifyContent="center" p="16">
        <Link
          appearance="default"
          fontSize="sm"
          href={`/instances/${instanceId}/notifications`}
          onClick={onViewNotifications}
        >
          View all notifications
        </Link>
      </Group>
    </Group>
  );
}

type NotificationTrayItemProps = {
  notification: Notification;
  onActivate: (notification: Notification) => void;
  onDelete: (id: string) => void;
};

function NotificationTrayItem({ notification, onActivate, onDelete }: NotificationTrayItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const timestamp = formatTimestamp(new Date(notification.created_at));

  return (
    <Group
      alignItems="start"
      bg={notification.is_read ? 'bg.default' : 'bg.secondary'}
      borderB="1"
      borderColor="border.tertiary"
      className={notificationListItemStyles}
      cursor="pointer"
      gap="12"
      onClick={() => onActivate(notification)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      p="16"
      transition="colors"
    >
      <NotificationTypeIcon type={notification.type} />

      <Group flex="1" flexDirection="column" gap="8">
        <Group alignItems="start" gap="16" justifyContent="space-between">
          <Text fontSize="md" fontWeight={notification.is_read ? '500' : '700'} lineClamp="2">
            {notification.title}
          </Text>
          <Group
            alignItems="center"
            flex="none"
            gap="8"
            justifyContent="end"
            style={{ minHeight: 28, minWidth: 64 }}
          >
            {isHovered ? (
              <Button
                appearance="subtle"
                aria-label={`Dismiss ${notification.title}`}
                icon={<IconXmark />}
                onClick={e => {
                  e.stopPropagation();
                  onDelete(notification.id);
                }}
                size="sm"
                square
              />
            ) : (
              <>
                <Text color="fg.tertiary" fontSize="xs" whiteSpace="nowrap">
                  {timestamp.time}
                </Text>
                {!notification.is_read && (
                  <Box bg="bg.error" flex="none" rounded="full" style={{ width: 8, height: 8 }} />
                )}
              </>
            )}
          </Group>
        </Group>
        <Text color="fg.secondary" fontSize="sm" lineClamp="3">
          {notification.message}
        </Text>
      </Group>
    </Group>
  );
}

function NotificationTypeIcon({ type }: { type: Notification['type'] }) {
  const visuals = getNotificationVisuals(type);
  if (!visuals) return null;
  return (
    <Box color={visuals.color} display="inline-flex" mt="2">
      <visuals.Icon />
    </Box>
  );
}
